import { useState } from "react";
import { useRhythm } from "./RhythmProvider";
import { Calendar, ChevronDown, Check } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const RHYTHM_OPTIONS = [
  { value: "weekly", label: "Weekly", desc: "Every 7 days" },
  { value: "fortnightly", label: "Fortnightly", desc: "Every 14 days" },
  { value: "monthly", label: "Monthly", desc: "1st to end of month" },
  { value: "per_pay_cycle", label: "Per pay cycle", desc: "Based on when you get paid" },
];

const DAY_OPTIONS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
];

const DATE_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}${["st","nd","rd"][((i+1)%100-20)%10-1] || ["st","nd","rd"][(i+1)%100-1] || "th"}`,
}));

export function CycleHeader() {
  const { rhythm, dateRange, label, updateRhythm, loading } = useRhythm();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(rhythm.rhythm_type);
  const [anchorType, setAnchorType] = useState(rhythm.pay_anchor_type || "date");
  const [anchorValue, setAnchorValue] = useState(rhythm.pay_anchor_value || "1");
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    setSelected(rhythm.rhythm_type);
    setAnchorType(rhythm.pay_anchor_type || "date");
    setAnchorValue(rhythm.pay_anchor_value || "1");
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      rhythm_type: selected,
      pay_anchor_type: selected === "per_pay_cycle" ? anchorType : null,
      pay_anchor_value: selected === "per_pay_cycle" ? anchorValue : null,
    };
    await updateRhythm(payload);
    setSaving(false);
    setOpen(false);
  };

  if (loading) return null;

  return (
    <>
      <div
        className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3 mb-4"
        data-testid="cycle-header"
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium text-foreground" data-testid="cycle-date-range">
              This cycle: {dateRange}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="cycle-rhythm-label">
              Rhythm: {label}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpen}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="cycle-change-btn"
        >
          Change
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choose your rhythm</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              How often do you want to review and reset?
            </p>
          </DialogHeader>

          <div className="space-y-2 my-4">
            {RHYTHM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSelected(opt.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${
                  selected === opt.value
                    ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                    : "border-border hover:border-muted-foreground/30"
                }`}
                data-testid={`rhythm-option-${opt.value}`}
              >
                <div>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </div>
                {selected === opt.value && (
                  <Check className="w-4 h-4 text-[#1a4d3e]" />
                )}
              </button>
            ))}
          </div>

          {selected === "per_pay_cycle" && (
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm font-medium">When does your income usually arrive?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAnchorType("date")}
                  className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${
                    anchorType === "date"
                      ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                      : "border-border"
                  }`}
                  data-testid="anchor-type-date"
                >
                  Specific date
                </button>
                <button
                  onClick={() => setAnchorType("day")}
                  className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${
                    anchorType === "day"
                      ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                      : "border-border"
                  }`}
                  data-testid="anchor-type-day"
                >
                  Day of week
                </button>
              </div>

              {anchorType === "date" ? (
                <div className="grid grid-cols-7 gap-1">
                  {DATE_OPTIONS.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => setAnchorValue(d.value)}
                      className={`p-1.5 rounded text-xs font-medium transition-colors ${
                        anchorValue === d.value
                          ? "bg-[#1a4d3e] text-white"
                          : "hover:bg-muted"
                      }`}
                      data-testid={`anchor-date-${d.value}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {DAY_OPTIONS.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => setAnchorValue(d.value)}
                      className={`w-full p-2 rounded-lg border text-left text-sm transition-colors ${
                        anchorValue === d.value
                          ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                      data-testid={`anchor-day-${d.value}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
              data-testid="rhythm-save-btn"
            >
              {saving ? "Saving..." : "Save rhythm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
